// V12 Retail Finance — "from £X/month" estimate, finance popup, and collection-grid badges
// Standalone (no shared deps) so it can load independently of section-product-main.js's script order.
function formatMoney(amount, format) {
  const formatted = parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (format || "£{{amount}}")
    .replace(/\{\{\s*amount\s*\}\}/, formatted)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(amount || 0)));
}

function v12Money(amount) {
  return formatMoney(amount, window.themeSettings?.moneyFormat).replace(/\.00(?=\D*$)/, '');
}

// The headline offer: lowest APR first, then the lowest monthly payment within that APR.
// Ranking on monthly payment alone picks the longest, interest-bearing term (e.g. 9.9%
// over 48 months) over the 0% plan the product page's calculator actually leads with.
function v12CalculateLowest(cashPrice) {
  try {
    const products = window.V12.getFinanceProducts();
    let best = null;
    products.forEach((p) => {
      const financeProduct = window.V12.getFinanceProduct(p.productId);
      const payments = window.V12.calculate(financeProduct, cashPrice, 0);
      if (!payments) return;
      const apr = parseFloat(payments.apr) || 0;
      const bestApr = best ? parseFloat(best.apr) || 0 : Infinity;
      if (!best || apr < bestApr || (apr === bestApr && payments.initialPayments < best.initialPayments)) {
        best = Object.assign({}, payments, { productId: p.productId });
      }
    });
    return best;
  } catch (err) {
    console.warn('V12 finance calculation failed:', err);
    return null;
  }
}

(function () {
  const calc = document.querySelector('[data-finance-calculator]');
  const modal = document.querySelector('[data-finance-modal]');
  if (!calc || !modal) return; // block absent, calculator disabled, or Retailer ID/GUID unset

  // Move the modal to a direct child of <body> so its `position: fixed` panel
  // is always positioned/stacked against the real viewport, regardless of any
  // sticky/positioned ancestor (e.g. the sticky product info column) it started in.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  const monthlyEl = calc.querySelector('[data-finance-monthly]');

  const openTrigger = document.querySelector('[data-finance-modal-open]');
  const closeEls = modal.querySelectorAll('[data-finance-modal-close]');
  const depositButtons = modal.querySelectorAll('[data-deposit-percent]');
  const depositSlider = modal.querySelector('[data-finance-deposit-slider]');
  const depositOutput = modal.querySelector('[data-finance-deposit-output]');
  const depositMin = modal.querySelector('[data-finance-deposit-min]');
  const depositMax = modal.querySelector('[data-finance-deposit-max]');
  const productSelect = modal.querySelector('[data-finance-product-select]');
  const calcBody = modal.querySelector('[data-finance-modal-calc]');
  const addToCartBtn = modal.querySelector('[data-finance-add-to-cart]');

  const state = {
    cashPrice: 0,
    depositPercent: 0,
    products: null,
    bestProductId: null,
  };

  const money = v12Money;

  // Mirrors V12's own reference rounding: round UP to the nearest penny.
  function depositAmount(cashPrice, percent) {
    return parseFloat((Math.ceil(cashPrice * percent) / 100).toFixed(2));
  }

  const calculateLowest = v12CalculateLowest;

  function renderHeadline(payments) {
    if (!payments || !monthlyEl) return;
    monthlyEl.textContent = `${money(payments.initialPayments)}/month`;
    calc.hidden = false;
    calc.parentElement.hidden = false;
  }

  // Sale items can be excluded from V12 (Theme settings → V12 Retail Finance →
  // "Hide on sale items: product cards and product page"), and on-sale is per variant
  const hideOnSale = calc.dataset.hideOnSale === 'true';

  function hideHeadline() {
    state.cashPrice = 0;
    calc.hidden = true;
    calc.parentElement.hidden = true;
  }

  function refresh(cashPricePence, onSale) {
    if (onSale && hideOnSale) {
      hideHeadline();
      return;
    }
    const cashPrice = parseInt(cashPricePence, 10) / 100; // pence -> pounds
    if (!cashPrice || !window.V12 || typeof window.V12.getFinanceProducts !== 'function') return;
    state.cashPrice = cashPrice;
    state.products = null; // re-populate the modal's product list next time it opens
    const best = calculateLowest(cashPrice);
    state.bestProductId = best ? best.productId : null;
    renderHeadline(best);
  }

  function populateProducts() {
    if (!productSelect || state.products) return;
    try {
      state.products = window.V12.getFinanceProducts() || [];
      productSelect.innerHTML = '';
      state.products.forEach((p) => {
        const option = document.createElement('option');
        option.value = p.productId;
        option.textContent = p.name;
        productSelect.appendChild(option);
      });
      // Open on the plan the headline quoted, so the two never disagree
      if (state.bestProductId != null) productSelect.value = String(state.bestProductId);
    } catch (err) {
      console.warn('V12 finance products unavailable:', err);
      state.products = [];
    }
  }

  const labels = calcBody
    ? {
        costPrice: calcBody.dataset.labelCostPrice || 'Cost Price',
        deposit: calcBody.dataset.labelDeposit || 'Deposit Payment',
        financeAmount: calcBody.dataset.labelFinanceAmount || 'Finance Amount',
        repaymentTerm: calcBody.dataset.labelRepaymentTerm || 'Repayment Term',
        interestCharged: calcBody.dataset.labelInterestCharged || 'Interest Charged',
        instalmentAmount: calcBody.dataset.labelInstalmentAmount || 'Instalment Amount',
        annualRate: calcBody.dataset.labelAnnualRate || 'Annual Rate Of Interest',
        totalPayable: calcBody.dataset.labelTotalPayable || 'Total Amount Payable',
        totalToday: calcBody.dataset.labelTotalToday || 'Total Payable Today',
      }
    : {};

  function renderCalculation() {
    if (!productSelect || !calcBody) return;
    const productId = productSelect.value;
    if (!productId || !window.V12) return;

    try {
      const financeProduct = window.V12.getFinanceProduct(productId);
      const deposit = depositAmount(state.cashPrice, state.depositPercent);
      const payments = window.V12.calculate(financeProduct, state.cashPrice, deposit);
      if (!payments) return;

      if (depositOutput) depositOutput.textContent = money(payments.deposit);

      calcBody.innerHTML = `
        <div class="v12-finance-modal__calc-row"><dt>${labels.costPrice}</dt><dd>${money(payments.cashPrice)}</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.deposit}</dt><dd>${money(payments.deposit)}</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.financeAmount}</dt><dd>${money(payments.loanAmount)}</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.repaymentTerm}</dt><dd>${payments.months} months</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.interestCharged}</dt><dd>${payments.apr}%</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.instalmentAmount}</dt><dd>${money(payments.initialPayments)}</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.annualRate}</dt><dd>${payments.annualRate}%</dd></div>
        <div class="v12-finance-modal__calc-row"><dt>${labels.totalPayable}</dt><dd>${money(payments.amountPayable)}</dd></div>
        <div class="v12-finance-modal__calc-row v12-finance-modal__calc-row--total"><dt>${labels.totalToday}</dt><dd>${money(payments.deposit)}</dd></div>
      `;
    } catch (err) {
      console.warn('V12 finance calculation failed:', err);
    }
  }

  function positionSliderBubble() {
    if (!depositSlider || !depositOutput) return;
    const min = parseFloat(depositSlider.min) || 0;
    const max = parseFloat(depositSlider.max) || 100;
    const percent = (state.depositPercent - min) / (max - min);
    const thumbWidth = 28; // matches the thumb width set in section-v12-finance.scss
    const offset = thumbWidth * (0.5 - percent);
    depositOutput.style.left = `calc(${percent * 100}% + ${offset}px)`;
  }

  function minDepositPercent() {
    return depositSlider ? parseFloat(depositSlider.min) || 0 : 0;
  }

  function maxDepositPercent() {
    return depositSlider ? parseFloat(depositSlider.max) || 50 : 50;
  }

  function setDepositPercent(percent) {
    state.depositPercent = Math.max(minDepositPercent(), Math.min(maxDepositPercent(), percent));
    if (depositSlider) depositSlider.value = state.depositPercent;
    positionSliderBubble();
    depositButtons.forEach((btn) => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.depositPercent, 10) === state.depositPercent);
    });
    renderCalculation();
  }

  function openModal() {
    if (!state.cashPrice) return;
    populateProducts();
    if (depositMin) depositMin.textContent = `Min: ${money(depositAmount(state.cashPrice, minDepositPercent()))}`;
    if (depositMax) depositMax.textContent = `Max: ${money(depositAmount(state.cashPrice, maxDepositPercent()))}`;
    setDepositPercent(parseFloat(calc.dataset.defaultDeposit) || minDepositPercent());
    if (addToCartBtn) {
      const productAddBtn = document.querySelector('[data-product-form] [data-add-to-cart]');
      addToCartBtn.disabled = !productAddBtn || productAddBtn.disabled;
    }
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    window.FocusTrap?.activate(modal.querySelector('.v12-finance-modal__panel'));
  }

  function closeModal() {
    modal.hidden = true;
    document.body.style.overflow = '';
    window.FocusTrap?.release();
    openTrigger?.focus();
  }

  function init(attempt) {
    attempt = attempt || 0;
    if (window.V12 && typeof window.V12.getFinanceProducts === 'function') {
      refresh(calc.dataset.cashPrice, calc.dataset.onSale === 'true');
      return;
    }
    if (attempt >= 10) return; // ~3s of retries; static fallback stays visible if V12 never initialises
    setTimeout(() => init(attempt + 1), 300);
  }
  init();

  openTrigger?.addEventListener('click', openModal);
  closeEls.forEach((el) => el.addEventListener('click', closeModal));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
  });
  depositButtons.forEach((btn) => {
    btn.addEventListener('click', () => setDepositPercent(parseInt(btn.dataset.depositPercent, 10)));
  });
  depositSlider?.addEventListener('input', () => setDepositPercent(parseInt(depositSlider.value, 10) || 0));
  productSelect?.addEventListener('change', renderCalculation);
  addToCartBtn?.addEventListener('click', () => {
    const productForm = document.querySelector('[data-product-form]');
    const productAddBtn = productForm?.querySelector('[data-add-to-cart]');
    if (!productForm || productAddBtn?.disabled) return;
    closeModal();
    productForm.requestSubmit(productAddBtn);
  });

  // Bridge for the variant-change handler in section-product-main.js
  window.__waUpdateFinanceEstimate = refresh;
})();

// V12 Retail Finance — collection/product-grid "from £X/month, Y% APR" badges.
// Runs independently of the popup above so it still works on pages with no
// finance modal at all (collection grids, search results, related products).
//
// Exposed as window.__waRenderV12GridBadges so collection/search AJAX (which
// swap the grid's innerHTML rather than reloading the page) can re-run it for
// the newly-inserted cards — this IIFE only ever sees the badges present at
// initial script load, so without that hook every card added after a filter,
// sort or page change would keep its badge blank.
(function () {
  function renderBadge(el) {
    const cashPrice = parseInt(el.dataset.cashPrice, 10) / 100; // pence -> pounds
    if (!cashPrice) return;
    const payments = v12CalculateLowest(cashPrice);
    if (!payments) return;
    el.textContent = `From ${v12Money(payments.initialPayments)}/month, ${payments.apr}% APR`;
  }

  function renderAll() {
    document.querySelectorAll('[data-v12-grid-badge]:empty').forEach(renderBadge);
  }

  function init(attempt) {
    attempt = attempt || 0;
    if (!document.querySelector('[data-v12-grid-badge]')) return;
    if (window.V12 && typeof window.V12.getFinanceProducts === 'function') {
      renderAll();
      return;
    }
    if (attempt >= 10) return; // ~3s of retries; badges stay hidden if V12 never initialises
    setTimeout(() => init(attempt + 1), 300);
  }
  init();

  window.__waRenderV12GridBadges = renderAll;
})();
